import React from 'react';
import PropTypes from 'prop-types';
import { DropTarget, DragSource } from 'react-dnd';
import Grid from '../../../fw/Grid.react';
import JSONTableNew from './JSONTableNew.react';
import { findDOMNode } from 'react-dom';
import {FormattedMessage, defineMessages, injectIntl, IntlShape} from 'react-intl';
import {Button, ButtonGroup, FormGroup, ControlLabel, FormControl, HelpBlock, ButtonToolbar} from 'react-bootstrap';
import {getLocalesMyAppSupports} from '../../../../common/locales';
import {getLocale} from '../../../locales';
import {safeJSONparse} from "../../../../common/util/safeJSONparse";

const messages = defineMessages({
	removeButtonTitle: {id: 'jsonTable.button.removeTitle', 'defaultMessage': 'Remove column from table'},
	renameButtonTitle: {id: 'jsonTable.button.renameTitle', 'defaultMessage': 'Rename column'},
	sortButtonTitle: {id: 'jsonTable.button.sortTitle', 'defaultMessage': 'Sort by this column'},
});

export class JSONTableDrop extends JSONTableNew {
	static propTypes = {
		canDrop: PropTypes.bool.isRequired,
		column: PropTypes.object.isRequired,
		columnOffer: PropTypes.array,
		connectDropTarget: PropTypes.func.isRequired,
		dragEndFn: PropTypes.func,
		draggingFn: PropTypes.func,
		draggingIndex: PropTypes.number,
		dropFn: PropTypes.func,
		index: PropTypes.number.isRequired,
		intl: PropTypes.object.isRequired,
		isGroupView: PropTypes.bool,
		isOver: PropTypes.bool.isRequired,
		order: PropTypes.array,
		orderChange: PropTypes.func.isRequired,
		removeColumn: PropTypes.func.isRequired,
		renameColumn: PropTypes.func.isRequired,
		rows: PropTypes.number.isRequired,
		viewWithData: PropTypes.object,
		width: PropTypes.number,
	}

	static defaultProps = {
		dragging: false,
	};

	constructor(props) {
		super(props);

		this.originName = JSONTableDrop.getName(props);
		this.state = {
			dragging: props.index === props.draggingIndex,
			name: safeJSONparse(JSON.stringify(this.originName)),
			overInput: false,
			renaming: false
		};
	}

	componentWillReceiveProps(nextProps) {
		if (this.props.isOver && !nextProps.isOver && nextProps.draggingIndex === null) {
			this.props.dragEndFn();
		}


		let state = {};
		let changed = false;

		if (this.state.dragging !== (nextProps.draggingIndex === nextProps.index)) {
			changed = true;
			state.dragging = nextProps.draggingIndex === nextProps.index;
		}

		let newName = JSONTableDrop.getName(nextProps);
		getLocalesMyAppSupports().forEach(locale => {
			if (newName[locale]  && newName[locale] !== this.originName[locale]) {
				this.originName[locale] = newName[locale];
				this.state.name[locale] = newName[locale];
			}
		});

		if (changed) {
			this.setState(state);
		}
	}

	static getName(props) {
		let name = {};

		if (props.column.object.name) {
			if (typeof props.column.object.name === "string") {
				name[getLocale()] = props.column.object.name;
			}
			else {
				name = props.column.object.name;
			}
		}
		else if (props.column.map.shortName_l && props.column.map.shortName_l[0].content) {
			props.column.map.shortName_l.forEach(shortName => {
				name[shortName.locale] = shortName.content;
			});
		}
		else if (props.column.map.name_l && props.column.map.name_l[0].content) {
			props.column.map.name_l.forEach(nameL => {
				name[nameL.locale] = nameL.content;
			});
		}
		else if (props.column.map.shortName) {
			getLocalesMyAppSupports().forEach(locale => {
				name[locale] = props.column.map.shortName;
			});
		}
		else {
			getLocalesMyAppSupports().forEach(locale => {
				name[locale] = props.column.map.name;
			});
		}

		return name;
	}

	getColumnsFromViewDef() {
		let self = this;

		let columnDef;
		if (this.props.isGroupView) {
			this.props.columnOffer.find(dataSource => {
				columnDef = dataSource.fields.find(column => column.fieldDef.realId === self.props.column.id);
				if (columnDef) {
					return true;
				}
			});
		}
		else {
			columnDef = this.props.columnOffer.filter(column => column !== null).find((column) => {
				return column.fieldDef.realId === self.props.column.id;
			});
		}

		columnDef = columnDef || {fieldDef: {}};
		let returnValue = {};
		columnDef.name = this.originName[getLocale()] || '';
		returnValue[columnDef.fieldDef.realId || columnDef.fieldDef.id] = columnDef;
		return returnValue;
	}

	getColumn() {
		let {column} = this.props;
		let columns = this.getColumnsFromViewDef();

		this.isOrder = false;

		if (this.props.order && this.props.order.length > 0 && this.props.order[0].column === column.id) {
			this.isOrder = true;
			this.asc = this.props.order[0].asc;
			columns[column.id].className = JSONTableDrop.getSortingClassName(this.props.order[0].asc);
		}
		else if (columns[column.id]) {
			columns[column.id].className = "";
		}

		let columnDefinition = {};
		columnDefinition[column.id] = columns[column.id];

		return columnDefinition;
	}

	removeColumn() {
		this.props.removeColumn(this.props.index);
	}

	renameColumn() {
		this.setState({
			renaming: true
		});
	}

	setSorting() {
		if (this.isOrder) {
			let orderObject = this.props.order;
			orderObject[0].asc = !orderObject[0].asc;
			this.props.orderChange(orderObject);
		}
		else {
			this.props.orderChange([{
				column: this.props.column.id,
				asc: true
			}]);
		}
	}

	nameChange(e) {
		let name = this.state.name;

		name[e.target.lang] = e.target.value;
		this.setState({
			name: name
		});
	}

	getValidationState() {
		let valid = true;

		getLocalesMyAppSupports().forEach(locale => {
			if (!this.state.name[locale]) {
				valid = false;
			}
		});

		return valid ? null : 'error';
	}

	renameDone() {
		this.props.renameColumn(this.props.column.id, this.state.name);

		this.setState({
			renaming: false
		});
	}

	cancelRename() {
		this.setState({
			name: this.originName,
			renaming: false,
		});
	}

	mouseOverInput() {
		this.setState({
			overInput: true
		});
	}

	mouseNotOverInput() {
		this.setState({
			overInput: false
		});
	}

	getRenameInputs() {
		// TODO single language support for screen settings
		let self = this;
		return getLocalesMyAppSupports().map(locale => {
			return (
				<div className="input-group" key={locale}>
					<span className="input-group-addon">{locale}</span>
					<FormControl
						lang={locale}
						onChange={self.nameChange.bind(self)}
						onMouseEnter={self.mouseOverInput.bind(self)}
						onMouseOut={self.mouseNotOverInput.bind(self)}
						placeholder="Name"
						type="text"
						value={self.state.name[locale]}
					/>
				</div>
			);
		});
	}

	render() {
		let invalidObject = '';
		if (this.props.column.invalid) {
			invalidObject = this.props.column.object;
			invalidObject.id = this.props.column.id;
			invalidObject = JSON.stringify(invalidObject);
		}
		let allowMove = !this.state.overInput;

		return (
			<small
				className={"JSONTableDropColumn jsonTable jsonTableNew" + (this.state.dragging ? ' dragging' : '') + (this.props.column.invalid ? ' invalid' : '')}
				ref={instance => {
					this.node = findDOMNode(instance);

					if (allowMove) {
						this.props.connectDragSource(this.node);
						this.props.connectDropTarget(this.node);
					}
				}}
				style={{maxWidth: this.props.width + '%'}}
			>
				{this.props.column.invalid
					? <span className="identification"><strong><FormattedMessage defaultMessage="Invalid column" id="jsonTable.invalidColumn" /></strong><br />{invalidObject}</span>
					: <Grid
						columns={this.getColumn()}
						getUrl={this.getUrl}
						hoverEffect={false}
						list={this.getPreviewData(10)}
						metadata={this.getMetaData()}
						render={this.renderPreview}
					/>
				}
				<div className={"rename " + (this.state.renaming ? "visible" : "hidden")}>
					<form>
						<FormGroup controlId="formBasicText" validationState={this.getValidationState()}>
							<ControlLabel><FormattedMessage defaultMessage="Custom name for column:" id="jsonTable.rename.title" /></ControlLabel>
							{ this.getRenameInputs() }
							<HelpBlock><FormattedMessage defaultMessage="This name will not persist when column is removed from table." id="jsonTable.rename.persistentWarning" /></HelpBlock>
						</FormGroup>
						<ButtonToolbar>
							<Button bsSize="small" bsStyle="primary" onClick={this.renameDone.bind(this)}><FormattedMessage defaultMessage="Rename" id="jsonTable.button.rename" /></Button>
							<Button bsSize="small" onClick={this.cancelRename.bind(this)}><FormattedMessage defaultMessage="Cancel" id="common.cancel" /></Button>
						</ButtonToolbar>
					</form>
				</div>
				<div className={"JSONColumnManipulation" + (this.state.renaming ? ' hidden' : '')}>
					<ButtonGroup vertical>
						{this.props.column.invalid ? null : <Button bsStyle="info" onClick={this.renameColumn.bind(this)} title={this.props.intl.formatMessage(messages.renameButtonTitle)}>
							<span className="fa fa-pencil"></span>
							<span className="title"><FormattedMessage defaultMessage="Rename" id="jsonTable.button.rename" /></span>
						</Button>}
						{this.props.column.invalid ? null : <Button bsStyle="success" onClick={this.setSorting.bind(this)} title={this.props.intl.formatMessage(messages.sortButtonTitle)}>
							<span className={"fa " + (this.asc === false ? "fa-sort-alpha-desc" : "fa-sort-alpha-asc")}></span>
							<span className="title"><FormattedMessage defaultMessage="Sort" id="jsonTable.button.sort" /></span>
						</Button>}
						<Button bsStyle="danger" onClick={this.removeColumn.bind(this)} title={this.props.intl.formatMessage(messages.removeButtonTitle)}>
							<span className="fa fa-times"></span>
							<span className="title"><FormattedMessage defaultMessage="Remove" id="jsonTable.button.remove" /></span>
						</Button>
					</ButtonGroup>
				</div>
			</small>
		);
	}
}

const dropHandler = {
	hover(props, monitor, component) {
		// hovering over itself
		if (props.dragging) {
			return;
		}

		const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();
		const clientOffset = monitor.getClientOffset();

		const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
		const hoverClientX = clientOffset.x - hoverBoundingRect.left;
		const dragItem = monitor.getItem();

		let targetIndex = props.index;

		if (hoverMiddleX < hoverClientX) {
			targetIndex++;
		}

		if (component.props.index < targetIndex) {
			targetIndex--;
		}

		props.draggingFn(dragItem, targetIndex, monitor.getItem().originalIndex);
	},
	drop(props, monitor, component) {
		props.dropFn(monitor.getItem(), component.props.index);
	}
};

function dropActions(connect, monitor) {
	return {
		canDrop: monitor.canDrop(),
		connectDropTarget: connect.dropTarget(),
		isOver: monitor.isOver(),
	};
}

const dragHandler = {
	beginDrag(props) {
		return {
			column: props.column,
			originalIndex: props.index,
		};
	},

	endDrag(props, monitor) {
		if (!monitor.didDrop()) {
			props.dragEndFn();
		}
	},
};

function dragActions(connect, monitor) {
	return {
		connectDragSource: connect.dragSource(),
		isDragging: monitor.isDragging()
	};
}

export default DragSource('JSONTableDragColumn', dragHandler, dragActions)(
	DropTarget('JSONTableDragColumn', dropHandler, dropActions)(injectIntl(JSONTableDrop))
);
